import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Option } from "react-bootstrap-typeahead/types/types";
import styled from "styled-components";
import {
  Sector,
  useLeerSectoresOrigen,
} from "../../../../services/paisesServicios";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import { Model } from "../Model";
import FechasTransporte from "../FechasTransporte/FechasTransporte";
import CalculoPrecioTransporte from "../CalculoPrecioTransporte/CalculoPrecioTransporte";
import FechasGuarderia from "../FechasGuarderia/FechasGuarderia";
import SuccessButton from "../../../organisms/SuccessButton";
import { useWizard } from "../../../../hooks/wizard";
import { fechasSonIguales } from "../../../../utils/dateUtils";
import { usePasoReservaGuarderia } from "../pasoReservaGuarderia";

/*
  Los campos visuales de transporte son lo del modelo siempre
  No puede avanzar si no hay datos de transporte definidos

  Si datos de transporte son indefinidos:
    - Si datos residencia son validos:
          - setear "si" transportar a residencia
          - setear datos de residencia como transporte
    - Si no son validos:
        - setear "no" transportar a residencia
        - setear indefinidos los datos de transporte

  Si datos de transporte estan definidos:
    - Si datos residencia son iguales a transporte:
      - setear "si" transportar a residencia
    - Si no son iguales:
      - setear "no" transportar a residencia
      - mostrar campos de transporte
*/

function DatosTransporte({ model }: { model: Model }) {
  const {
    currentModel,
    definirDireccionTransporte,
    definirContrataIda,
    definirContrataVuelta,
    definirSectorTransporte,
  } = usePasoReservaGuarderia(model);
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const datosPropietario = model.datosPropietario!!;

  const [usarDireccionResidencia, setUsarDireccionResidencia] =
    useState<boolean>(definirUsaTransporteResidenciaInicial(model));

  const [sectorTransporteInicial, setSectorTransporteInicial] =
    useState<Option | null>(obtenerSectorInicial(model));

  const {
    data: sectoresOrigen,
    isSuccess: sectoresIsSuccess,
    error: errorSectoresOrigen,
    isLoading: sectoresOrigenIsLoading,
  } = useLeerSectoresOrigen();

  const error = errorSectoresOrigen;

  const sectoresOptions: Option[] = useMemo(() => {
    if (sectoresIsSuccess && sectoresOrigen) {
      return sectoresOrigen.map((s) => ({ id: s.id, nombre: s.nombre }));
    }
    return [];
  }, [sectoresIsSuccess, sectoresOrigen]);

  useEffect(() => {
    if (
      datosResidenciaSonValidosParaTransporte(model) &&
      !model.direccionTransporte
    ) {
      definirDireccionTransporte({
        direccion: model.datosPropietario!!.direccionResidencia!!,
      });
      definirSectorTransporte({
        sector: model.datosPropietario!!.sectorResidencia!!,
      });
    }
  }, [model, definirDireccionTransporte, definirSectorTransporte]);

  const onUsarDirreccionResidenciaChange = () => {
    const usara = !usarDireccionResidencia;
    if (usara) {
      if (datosResidenciaSonValidosParaTransporte(model)) {
        definirDireccionTransporte({
          direccion: model.datosPropietario!!.direccionResidencia!!,
        });
        definirSectorTransporte({
          sector: model.datosPropietario!!.sectorResidencia!!,
        });
        setSectorTransporteInicial({
          id: model.datosPropietario!!.sectorResidencia!!.id,
          nombre: model.datosPropietario!!.sectorResidencia!!!.nombre,
        } as Option);
      }
    } else {
      definirDireccionTransporte({
        direccion: undefined,
      });
      definirSectorTransporte({
        sector: undefined,
      });
      setSectorTransporteInicial(null);
    }
    setUsarDireccionResidencia(usara);
  };

  const puedeAvanzar =
    !!currentModel.direccionTransporte && !!currentModel.sectorTransporte;

  const onIdaChange = () => {
    definirContrataIda({ contrata: !currentModel.contrataIda });
  };

  const onVueltaChange = () => {
    definirContrataVuelta({ contrata: !currentModel.contrataVuelta });
  };

  const onDireccionTransporteChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    definirDireccionTransporte({ direccion: event.target.value });
  };

  const onSectorTransporteChange = (selected: Option[]) => {
    if (selected.length === 1) {
      const rec = selected[0] as Record<string, any>;
      const nuevoSector: Sector = {
        id: rec.id,
        nombre: rec.nombre,
      };
      definirSectorTransporte({ sector: nuevoSector });
    }
  };

  if (error) {
    return <ServerErrorModal />;
  }

  if (avanza && !fechasReservaEstanTodasContratadas(currentModel)) {
    return <FechasTransporte model={currentModel} />;
  }

  if (avanza && currentModel.fechasTransporte.length > 0) {
    return <CalculoPrecioTransporte model={currentModel} />;
  }

  if (vuelve) {
    return <FechasGuarderia model={currentModel} />;
  }

  return (
    <Row className="justify-content-center">
      <Col className="col-10">
        <HeaderSection className="pt-4 pb-4">
          <Title>
            Contratación de transporte{" "}
            <IconContainer>
              <AirportShuttleIcon sx={{ fontSize: 30 }} />
            </IconContainer>
          </Title>
        </HeaderSection>
        <Row>
          <section className="mb-3">
            <h5>Datos de residencia</h5>
            <div className="mt-3">
              <label className="mb-2">
                <strong>Propietario: </strong>{" "}
                <NombrePropietario
                  primerNombre={datosPropietario.primerNombre}
                  primerApellido={datosPropietario.primerApellido}
                />
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Ciudad de residencia: </strong>{" "}
                {datosPropietario.ciudadResidencia?.nombre}
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Sector de residencia: </strong>{" "}
                {datosPropietario.sectorResidencia?.nombre}
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Dirección de residencia: </strong>{" "}
                {datosPropietario.direccionResidencia}
              </label>
            </div>
          </section>
          <section className="mt-2 mb-2">
            <h5>Opciones</h5>
            {datosResidenciaSonValidosParaTransporte(model) && (
              <div className="mb-4 mt-3">
                <label className="mb-1">
                  Transportar a dirección de residencia
                </label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckDefault"
                    checked={usarDireccionResidencia}
                    onChange={onUsarDirreccionResidenciaChange}
                  />
                  <label>{usarDireccionResidencia ? "Si" : "No"} </label>
                </div>
              </div>
            )}

            {!usarDireccionResidencia && (
              <div className="mb-4">
                {model.ciudadOrigen && (
                  <p>
                    <i>
                      El transporte se realiza en la ciudad de
                      {" " + model.ciudadOrigen.nombre}
                    </i>
                  </p>
                )}
                <div className="mb-2">
                  <label
                    className="form-label"
                    htmlFor="direccion-transporte-input"
                  >
                    Dirección de transporte:
                  </label>
                  <input
                    id="direccion-transporte-input"
                    type="text"
                    value={currentModel.direccionTransporte}
                    className="form-control"
                    onChange={onDireccionTransporteChange}
                  ></input>
                </div>
                <div className="mb-2">
                  <label
                    className="form-label"
                    htmlFor="sector-transporte-input"
                  >
                    Sector de transporte:
                  </label>
                  {sectorTransporteInicial ? (
                    <Typeahead
                      defaultSelected={[sectorTransporteInicial]}
                      clearButton
                      id="input-sector"
                      options={[sectorTransporteInicial]}
                      labelKey="nombre"
                      onChange={onSectorTransporteChange}
                      isLoading={sectoresOrigenIsLoading}
                    />
                  ) : (
                    <Typeahead
                      clearButton
                      id="input-sector"
                      options={sectoresOptions}
                      labelKey="nombre"
                      onChange={onSectorTransporteChange}
                      isLoading={sectoresOrigenIsLoading}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="mb-2">
              <label>
                Ida:
                <StyledCheck
                  type="checkbox"
                  className="form-check-input ms-2"
                  checked={currentModel.contrataIda}
                  onChange={onIdaChange}
                />
              </label>
            </div>
            <div className="mb-2">
              <label>
                Vuelta:
                <StyledCheck
                  type="checkbox"
                  className="form-check-input ms-2"
                  checked={currentModel.contrataVuelta}
                  onChange={onVueltaChange}
                />
              </label>
            </div>

          </section>
          <Row className="mb-2 justify-content-between mt-2">
            <Col>
              <Button
                type={"button"}
                style={{ width: "fit-content" }}
                onClick={volver}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                Volver
              </Button>
            </Col>
            <Col className="d-flex justify-content-end">
              <SuccessButton
                type={"button"}
                style={{ width: "fit-content" }}
                disabled={!puedeAvanzar}
                onClick={avanzar}
              >
                Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </SuccessButton>
            </Col>
          </Row>
        </Row>
      </Col>
    </Row>
  );
}

const NombrePropietario = ({
  primerNombre,
  primerApellido,
}: {
  primerNombre: string;
  primerApellido: string;
}) => {
  return <>{primerNombre + " " + primerApellido}</>;
};

const StyledCheck = styled("input")`
  display: inline-block;
  vertical-align: middle;
  margin-top: 0;
`;

const IconContainer = styled("span")`
  display: relative;
  color: ${(props) => props.theme.colors.main};
`;

const fechasReservaEstanTodasContratadas = (model: Model): boolean => {
  return fechasSonIguales(model.fechasProgramadas, model.fechasTransporte);
};

const direccionTransporteEsResidencia = (model: Model): boolean => {
  return (
    model.sectorTransporte !== undefined &&
    model.direccionTransporte !== undefined &&
    model.datosPropietario !== undefined &&
    model.datosPropietario.direccionResidencia !== undefined &&
    model.datosPropietario.sectorResidencia !== undefined &&
    model.datosPropietario.direccionResidencia === model.direccionTransporte &&
    model.datosPropietario.sectorResidencia.id === model.sectorTransporte.id
  );
};

const definirUsaTransporteResidenciaInicial = (model: Model): boolean => {
  if (model.sectorTransporte && model.direccionTransporte) {
    return direccionTransporteEsResidencia(model);
  }
  return datosResidenciaSonValidosParaTransporte(model);
};

const obtenerSectorInicial = (model: Model): Option | null => {
  if (model.sectorTransporte !== undefined) {
    return {
      id: model.sectorTransporte!.id,
      nombre: model.sectorTransporte!.nombre,
    } as Option;
  }
  const datosPropietario = model.datosPropietario!!;
  if (datosResidenciaSonValidosParaTransporte(model)) {
    return {
      id: datosPropietario.sectorResidencia!!.id,
      nombre: datosPropietario.sectorResidencia!!.nombre,
    } as Option;
  }
  return null;
};

const datosResidenciaSonValidosParaTransporte = (model: Model): boolean => {
  const datosPropietario = model.datosPropietario!!;
  const ciudadOrigen = model.ciudadOrigen!!;

  return (
    !!datosPropietario.direccionResidencia &&
    !!datosPropietario.sectorResidencia &&
    !!ciudadOrigen &&
    !!datosPropietario.ciudadResidencia &&
    !!datosPropietario.ciudadResidencia.id === !!ciudadOrigen.id
  );
};

export default DatosTransporte;
